import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";

import { linkIcon as LinkIcon } from "../../assets/icons";
import { useNotify } from "../../context/NotificationContext";
import {
  fetchStudentAssignments,
  fetchStudentLesson,
} from "../../services/studentPortalService/studentPortalService";
import type {
  StudentAssignment,
  StudentAssignmentStatus,
  StudentLesson,
} from "../../services/studentPortalService/studentPortalService.types";
import { logger } from "../../utils/logger";
import StudentBreadcrumbs from "./StudentBreadcrumbs";
import StudentMediaList from "./StudentMediaList";
import StudentVideoSplitView, { type StudentVideoSelection } from "./StudentVideoSplitView";
import {
  AssignmentDescription,
  AssignmentList,
  AssignmentMain,
  AssignmentRow,
  AssignmentTitle,
  CourseLessonPageShell,
  DemoEmptyState,
  DemoStatusChip,
  EntityMeta,
  EntityTitle,
  LessonChevron,
  LessonContentSection,
  MediaAction,
  MediaIcon,
  MediaMain,
  MediaName,
  MediaRowLink,
  MediaSub,
  RichLessonText,
  SectionHeading,
  SummaryValue,
  type StudentStatusTone,
} from "./StudentCourseLesson.styles";
import { EmptyState, LoadingState } from "./studentPortal.styles";

type LessonRouteState = {
  lessonCount?: number;
  courseTitle?: string;
};

const ASSIGNMENT_STATUS: Record<StudentAssignmentStatus, { label: string; tone: StudentStatusTone }> = {
  not_submitted: { label: "Не отправлено", tone: "neutral" },
  review: { label: "На проверке", tone: "info" },
  revision: { label: "Нужна доработка", tone: "warning" },
  rated: { label: "Проверено", tone: "success" },
};

const plainText = (value: string) => value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

const StudentLessonPage: React.FC = () => {
  const { courseId, lessonId } = useParams();
  const location = useLocation();
  const routeState = (location.state || {}) as LessonRouteState;
  const courseIdNumber = Number(courseId);
  const lessonIdNumber = Number(lessonId);
  const [lesson, setLesson] = useState<StudentLesson | null>(null);
  const [assignments, setAssignments] = useState<StudentAssignment[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<StudentVideoSelection | null>(null);
  const [loading, setLoading] = useState(true);
  const toast = useNotify();

  useEffect(() => {
    setSelectedVideo(null);
  }, [lessonIdNumber]);

  useEffect(() => {
    if (!courseIdNumber || !lessonIdNumber) return;
    let active = true;
    Promise.all([
      fetchStudentLesson(courseIdNumber, lessonIdNumber),
      fetchStudentAssignments(lessonIdNumber),
    ])
      .then(([lessonData, assignmentData]) => {
        if (!active) return;
        setLesson(lessonData);
        setAssignments(assignmentData);
      })
      .catch((error) => {
        logger.error("Ошибка загрузки урока:", error);
        toast.show({ severity: "error", summary: "Ошибка", detail: "Не удалось загрузить урок", life: 3000 });
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [courseIdNumber, lessonIdNumber, toast]);

  if (loading) return <LoadingState>Загружаем урок…</LoadingState>;
  if (!lesson) return <EmptyState>Урок недоступен.</EmptyState>;

  const lessonPosition = routeState.lessonCount ? ` из ${routeState.lessonCount}` : "";
  const breadcrumbCourseTitle =
    lesson.course_title?.trim() ||
    routeState.courseTitle?.trim() ||
    assignments[0]?.course_title?.trim() ||
    "Курс";
  const additionalMaterial = lesson.video_url ? (
    <MediaRowLink href={lesson.video_url} target="_blank" rel="noreferrer">
      <MediaIcon><LinkIcon aria-hidden="true" /></MediaIcon>
      <MediaMain>
        <MediaName>Дополнительный материал</MediaName>
        <MediaSub>ссылка</MediaSub>
      </MediaMain>
      <MediaAction>Открыть</MediaAction>
    </MediaRowLink>
  ) : null;

  return (
    <StudentVideoSplitView video={selectedVideo} onClose={() => setSelectedVideo(null)}>
      <CourseLessonPageShell>
        <StudentBreadcrumbs
          items={[
            { label: "Мои курсы", to: "/student/courses" },
            { label: breadcrumbCourseTitle, to: `/student/courses/${courseIdNumber}` },
            { label: lesson.title },
          ]}
        />

        <EntityTitle>{lesson.title}</EntityTitle>
        <EntityMeta>
          Урок {lesson.order}{lessonPosition}{lesson.category_title ? ` · ${lesson.category_title}` : ""}
        </EntityMeta>

        <SectionHeading>Материал урока</SectionHeading>
        <LessonContentSection>
          {lesson.content ? (
            <RichLessonText dangerouslySetInnerHTML={{ __html: lesson.content }} />
          ) : (
            <SummaryValue>Текстового материала нет.</SummaryValue>
          )}

          <StudentMediaList
            items={lesson.files_from_storage}
            lessonId={lesson.id}
            variant="lesson"
            leadingItem={additionalMaterial}
            selectedVideoId={selectedVideo?.id}
            onVideoSelect={(video) =>
              setSelectedVideo({
                ...video,
                contextLabel: "Видео из материалов урока. Содержимое урока остаётся доступным слева во время просмотра.",
              })
            }
          />
        </LessonContentSection>

        <SectionHeading>Домашние задания</SectionHeading>
        {assignments.length === 0 ? (
          <DemoEmptyState>К этому уроку домашних заданий нет.</DemoEmptyState>
        ) : (
          <AssignmentList>
            {assignments.map((assignment) => {
              const status = ASSIGNMENT_STATUS[assignment.status];
              return (
                <AssignmentRow key={assignment.id} to={`/student/assignments/${assignment.id}`}>
                  <AssignmentMain>
                    <AssignmentTitle>{assignment.title}</AssignmentTitle>
                    {assignment.content ? (
                      <AssignmentDescription>{plainText(assignment.content)}</AssignmentDescription>
                    ) : null}
                  </AssignmentMain>
                  <DemoStatusChip $tone={status.tone}>{status.label}</DemoStatusChip>
                  <LessonChevron aria-hidden="true" />
                </AssignmentRow>
              );
            })}
          </AssignmentList>
        )}
      </CourseLessonPageShell>
    </StudentVideoSplitView>
  );
};

export default StudentLessonPage;
